import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui';
import { AppsFilterButton, AppsMenuOption } from './AppsFilterButton';
import type {
    AppsCatalogFilterValue,
    AppsScopeFilterValue,
    AppsStatusFilterValue,
    AppsTableVM,
} from './useAppsTable';
import styles from './AppsToolbar.module.css';
import menuStyles from './appsMenu.module.css';

/**
 * EAA-1817: the toolbar row of `/console/apps` (frame 45) — search, the
 * Trạng thái/Danh mục/Phạm vi facet pills, "Xoá bộ lọc" and the right-aligned
 * "Hiển thị n / total" count. Copy and layout follow that export; see
 * `AppsFilterButton`'s doc for why the facet pills are native popovers rather
 * than antd ones.
 */
export function AppsToolbar({ vm }: { vm: AppsTableVM }) {
    const { t } = useTranslation();

    const STATUS_LABELS: Record<AppsStatusFilterValue, string> = {
        enabled: t('console.apps.status.enabled'),
        disabled: t('console.apps.status.disabled'),
    };
    const CATALOG_LABELS: Record<AppsCatalogFilterValue, string> = {
        ok: t('console.apps.catalogStatus.ok'),
        never: t('console.apps.catalogStatus.never'),
        failed: t('console.apps.catalogStatus.failed'),
    };
    const SCOPE_LABELS: Record<AppsScopeFilterValue, string> = {
        all_orgs: t('console.apps.scope.allOrgs'),
        selected_orgs: t('console.apps.scope.selectedOrgs'),
    };

    return (
        <div className={styles.toolbar}>
            {/* EAA-1817: raw <input> with a hand-drawn magnifier → kit Input, icon
                moved into its `prefix` slot — the shape every search box in the
                console uses. */}
            <Input
                className={styles.search}
                classNames={{ input: styles.searchInput }}
                value={vm.search}
                onChange={(event) => vm.setSearch(event.target.value)}
                placeholder={t('console.apps.toolbar.searchPlaceholder')}
                aria-label={t('console.apps.toolbar.searchAriaLabel')}
                prefix={
                    <svg
                        viewBox="0 0 24 24"
                        width="15"
                        height="15"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1.8}
                        aria-hidden="true"
                    >
                        <circle cx="11" cy="11" r="7" />
                        <path d="m20 20-3.5-3.5" strokeLinecap="round" />
                    </svg>
                }
            />

            <AppsFilterButton
                label={t('console.apps.toolbar.statusFilterLabel')}
                icon="add-leading"
                active={vm.statusFilter !== null}
                value={vm.statusFilter ? STATUS_LABELS[vm.statusFilter] : undefined}
                count={vm.statusFilter ? vm.statusCounts[vm.statusFilter] : undefined}
                open={vm.openFilter === 'status'}
                onOpenChange={(open) => vm.setOpenFilter(open ? 'status' : null)}
            >
                <div className={menuStyles.menu}>
                    <span className={menuStyles.menuLabel}>
                        {t('console.apps.toolbar.statusFilterLabel')}
                    </span>
                    <AppsMenuOption
                        checked={vm.statusFilter === null}
                        label={t('console.apps.toolbar.allOption')}
                        count={vm.statusCounts.enabled + vm.statusCounts.disabled}
                        onSelect={() => {
                            vm.setStatusFilter(null);
                            vm.setOpenFilter(null);
                        }}
                    />
                    {(['enabled', 'disabled'] as AppsStatusFilterValue[]).map((value) => (
                        <AppsMenuOption
                            key={value}
                            checked={vm.statusFilter === value}
                            label={STATUS_LABELS[value]}
                            count={vm.statusCounts[value]}
                            onSelect={() => {
                                vm.setStatusFilter(value);
                                vm.setOpenFilter(null);
                            }}
                        />
                    ))}
                </div>
            </AppsFilterButton>

            <AppsFilterButton
                label={t('console.apps.toolbar.catalogFilterLabel')}
                icon="add-leading"
                active={vm.catalogFilter !== null}
                value={vm.catalogFilter ? CATALOG_LABELS[vm.catalogFilter] : undefined}
                count={vm.catalogFilter ? vm.catalogCounts[vm.catalogFilter] : undefined}
                open={vm.openFilter === 'catalog'}
                onOpenChange={(open) => vm.setOpenFilter(open ? 'catalog' : null)}
            >
                <div className={menuStyles.menu}>
                    <span className={menuStyles.menuLabel}>
                        {t('console.apps.toolbar.catalogMenuLabel')}
                    </span>
                    <AppsMenuOption
                        checked={vm.catalogFilter === null}
                        label={t('console.apps.toolbar.allOption')}
                        count={
                            vm.catalogCounts.ok + vm.catalogCounts.never + vm.catalogCounts.failed
                        }
                        onSelect={() => {
                            vm.setCatalogFilter(null);
                            vm.setOpenFilter(null);
                        }}
                    />
                    {(['ok', 'never', 'failed'] as AppsCatalogFilterValue[]).map((value) => (
                        <AppsMenuOption
                            key={value}
                            checked={vm.catalogFilter === value}
                            label={CATALOG_LABELS[value]}
                            count={vm.catalogCounts[value]}
                            onSelect={() => {
                                vm.setCatalogFilter(value);
                                vm.setOpenFilter(null);
                            }}
                        />
                    ))}
                </div>
            </AppsFilterButton>

            <AppsFilterButton
                label={t('console.apps.toolbar.scopeFilterLabel')}
                icon="chevron-trailing"
                active={vm.scopeFilter !== null}
                value={vm.scopeFilter ? SCOPE_LABELS[vm.scopeFilter] : t('console.apps.toolbar.allOption')}
                count={
                    vm.scopeFilter
                        ? vm.scopeCounts[vm.scopeFilter]
                        : vm.scopeCounts.all_orgs + vm.scopeCounts.selected_orgs
                }
                open={vm.openFilter === 'scope'}
                onOpenChange={(open) => vm.setOpenFilter(open ? 'scope' : null)}
            >
                <div className={menuStyles.menu}>
                    <span className={menuStyles.menuLabel}>
                        {t('console.apps.toolbar.scopeMenuLabel')}
                    </span>
                    <AppsMenuOption
                        checked={vm.scopeFilter === null}
                        label={t('console.apps.toolbar.allOption')}
                        count={vm.scopeCounts.all_orgs + vm.scopeCounts.selected_orgs}
                        onSelect={() => {
                            vm.setScopeFilter(null);
                            vm.setOpenFilter(null);
                        }}
                    />
                    {(['all_orgs', 'selected_orgs'] as AppsScopeFilterValue[]).map((value) => (
                        <AppsMenuOption
                            key={value}
                            checked={vm.scopeFilter === value}
                            label={SCOPE_LABELS[value]}
                            count={vm.scopeCounts[value]}
                            onSelect={() => {
                                vm.setScopeFilter(value);
                                vm.setOpenFilter(null);
                            }}
                        />
                    ))}
                </div>
            </AppsFilterButton>

            {vm.activeFacetCount > 0 && (
                <span className={styles.facetCount}>
                    {t('console.apps.toolbar.facetCount', { count: vm.activeFacetCount })}
                </span>
            )}

            {vm.filtersActive && (
                <button type="button" className={styles.clear} onClick={vm.clearFilters}>
                    <svg
                        viewBox="0 0 24 24"
                        width="14"
                        height="14"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        aria-hidden="true"
                    >
                        <path d="M6 6l12 12M18 6 6 18" strokeLinecap="round" />
                    </svg>
                    {t('console.apps.toolbar.clearFiltersButton')}
                </button>
            )}

            <span className={styles.spacer} />

            <span className={styles.count}>
                {t('console.apps.toolbar.visibleCountPrefix')}{' '}
                <strong className={styles.countValue}>{vm.visibleApps.length}</strong> / {vm.totalCount}
            </span>
        </div>
    );
}
